// Ticket lists (open / closed / all) with search + filters by client, platform
// (brand) and status, plus sortable columns — including live timers: time since
// the ticket was opened, time to resolution, and SLA remaining/overdue ("en
// quema"). Each row links to the management detail view.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  listTickets, getSettings, formatDate, formatMins, tsToDate,
  type Ticket, type ServiceDeskSettings, type SlaState,
} from "../services/serviceDeskService";
import { SlaBadge, PriorityBadge, StatusBadge, AiAgentBadge, ReworkChip, SampleChip } from "../components/Badges";
import { statusFallback } from "../services/ticketClosure";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import { SERVICE_DESK_BASE } from "../config";

type Scope = "open" | "closed" | "all";
type SortKey = "client" | "brand" | "status" | "priority" | "sla" | "age" | "resolution" | "created";

const PRIORITY_RANK: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };

// Per-ticket derived timers (minutes). null = not applicable.
interface Row extends Ticket {
  createdMs: number | null;
  ageMins: number | null;          // time since opened (or until resolution if closed)
  resolutionMins: number | null;   // time to resolution (resolved − created)
  slaRemainingMins: number | null; // due − now; negative = vencido / "en quema"
}

function buildRow(t: Ticket, now: number): Row {
  const createdMs = tsToDate(t.createdAt)?.getTime() ?? null;
  const endMs = tsToDate(t.resolvedAt)?.getTime() ?? tsToDate(t.closedAt)?.getTime() ?? null;
  const ageMins = createdMs != null ? Math.round(((endMs ?? now) - createdMs) / 60000) : null;
  const resolutionMins = createdMs != null && endMs != null ? Math.round((endMs - createdMs) / 60000) : null;
  const dueRes = t.slaStatus?.dueAt?.resolution ?? null;
  const slaRemainingMins = dueRes != null && !endMs ? Math.round((dueRes - now) / 60000) : null;
  return { ...t, createdMs, ageMins, resolutionMins, slaRemainingMins };
}

// ---------------------------------------------------------------------------
// Qué pinta la columna SLA (#410).
//
// Antes se mostraba el restante con signo tal cual ("-2 h") JUNTO a la insignia
// que devuelve el servidor, y esa insignia puede decir "En tiempo" aunque el
// restante sea negativo: `computeSlaStatus` congela el badge en `on_time`
// mientras el reloj está en pausa, y los tickets importados / con el reloj
// desfasado llegan con vencimientos ya pasados. El resultado era la
// contradicción del issue: «SLA: En tiempo  -2 h».
//
// Regla: un restante negativo NUNCA se pinta como número negativo.
//   • en pausa           → "En pausa", sin número (el reloj no corre)
//   • restante < 0       → insignia "Incumplido" + "vencido hace 2 h"
//   • restante >= 0      → insignia del servidor + el tiempo que queda
// Pura y exportada para poder probarla sin montar la tabla.
// ---------------------------------------------------------------------------
export function slaCell(
  state: SlaState | undefined,
  paused: boolean | undefined,
  remainingMins: number | null,
): { state: SlaState | undefined; text: string | null; overdue: boolean } {
  if (paused) return { state, text: "En pausa", overdue: false };
  if (remainingMins == null) return { state, text: null, overdue: false };
  if (remainingMins < 0) {
    return { state: "breached", text: `vencido hace ${formatMins(-remainingMins)}`, overdue: true };
  }
  return { state, text: formatMins(remainingMins), overdue: false };
}

export default function TicketsListSection() {
  const { t } = useTranslation("serviceDesk");
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<Scope>("open");
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [status, setStatus] = useState("");
  const [client, setClient] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  // Sin esto, un 403 de agente dejaba la tabla en "Sin tickets.": el listado
  // vacío no distinguía "no hay nada" de "no tienes acceso".
  const { error: loadError, capture, reset } = useDeskLoadError();

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
    reset();
    const params: Record<string, string | number> = { scope, limit: 200 };
    if (q) params.q = q;
    if (brand) params.brand = brand;
    if (status) params.status = status;
    const handle = setTimeout(() => {
      listTickets(params).then((r) => setTickets(r.data)).catch(capture).finally(() => setLoading(false));
    }, q ? 300 : 0);
    return () => clearTimeout(handle);
  }, [scope, q, brand, status, capture, reset]);

  const now = Date.now();

  // Distinct clients (requesters) present in the current result set.
  const clients = useMemo(() => {
    const names = new Set<string>();
    tickets.forEach((t) => { const n = t.requester?.name?.trim(); if (n) names.add(n); });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [tickets]);

  const rows = useMemo(() => {
    let r = tickets.map((t) => buildRow(t, now));
    if (client) r = r.filter((t) => (t.requester?.name || "") === client);

    const val = (row: Row): string | number => {
      switch (sort.key) {
        case "client": return (row.requester?.name || "").toLowerCase();
        case "brand": return (row.brandLabel || row.brand || "").toLowerCase();
        case "status": return (row.statusLabel || row.status || "").toLowerCase();
        case "priority": return PRIORITY_RANK[row.priority] ?? 0;
        case "sla": return row.slaRemainingMins ?? Number.POSITIVE_INFINITY;
        case "age": return row.ageMins ?? -1;
        case "resolution": return row.resolutionMins ?? Number.POSITIVE_INFINITY;
        case "created": return row.createdMs ?? 0;
      }
    };
    return [...r].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va < vb) return -1 * sort.dir;
      if (va > vb) return 1 * sort.dir;
      return 0;
    });
  }, [tickets, client, sort, now]);

  // El filtro por cliente y el orden se aplican EN EL CLIENTE, sin volver a
  // pedir la lista, así que el efecto de carga de arriba no los ve. Sin este
  // reset la tabla podía quedarse vacía en una página fuera de rango.
  useEffect(() => { setCurrentPage(1); }, [client, sort]);

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  // Si la página actual quedara fuera de rango por cualquier otro camino, se
  // pinta la última en vez de una tabla vacía.
  const safePage = Math.min(currentPage, totalPages);
  // Filas paginadas
  const paginatedRows = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    return rows.slice(startIndex, startIndex + itemsPerPage);
  }, [rows, safePage]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: key === "created" || key === "age" ? -1 : 1 }));

  const statusColor = (id: string) => (settings?.statuses || []).find((s) => s.id === id)?.color ?? statusFallback(id)?.color;
  const text = "text-content";
  const card = "bg-surface border-line";

  const selectCls = `rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content`;

  return (
    <div className="p-6">
      <h1 className={`mb-4 text-xl font-bold ${text}`}>{t("ticketsList.title")}</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className={`flex rounded-lg border p-0.5 border-line`}>
          {(["open", "closed", "all"] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`rounded-md px-3 py-1 text-sm font-medium ${scope === s ? "bg-primary-500 text-white" : "text-content-muted"}`}
            >
              {s === "open" ? t("ticketsList.open") : s === "closed" ? t("ticketsList.closed") : t("ticketsList.all")}
            </button>
          ))}
        </div>
        <input
          className={`flex-1 min-w-[200px] rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content placeholder:!text-content-faint`}
          placeholder={t("ticketsList.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className={selectCls} value={client} onChange={(e) => setClient(e.target.value)}>
          <option value="">{t("ticketsList.allClients")}</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={selectCls} value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">{t("ticketsList.allPlatforms")}</option>
          {(settings?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
        <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("ticketsList.allStatuses")}</option>
          {(settings?.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      <div className={`rounded-xl border ${card}`}>
        <div className="overflow-x-auto">
          {loadError ? (
            <DeskLoadErrorPanel error={loadError} />
          ) : loading ? (
            <p className="p-8 text-center text-gray-400">{t("ticketsList.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-gray-400">{t("ticketsList.noTickets")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-app">
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="px-4 py-2">{t("ticketsList.table.reference")}</th>
                  <th className="px-4 py-2">{t("ticketsList.table.subject")}</th>
                  <Th label={t("ticketsList.table.client")} col="client" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.platform")} col="brand" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.status")} col="status" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.priority")} col="priority" sort={sort} onSort={toggleSort} />
                  <Th label={t("ticketsList.table.sla")} col="sla" sort={sort} onSort={toggleSort} title={t("ticketsList.table.slaTitle")} />
                  <Th label={t("ticketsList.table.openedAgo")} col="age" sort={sort} onSort={toggleSort} title={t("ticketsList.table.openedAgoTitle")} />
                  <Th label={t("ticketsList.table.resolution")} col="resolution" sort={sort} onSort={toggleSort} title={t("ticketsList.table.resolutionTitle")} />
                  <Th label={t("ticketsList.table.created")} col="created" sort={sort} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((ticket) => {
                const sla = slaCell(
                  (ticket.slaStatus?.overall || ticket.sla?.overall) as SlaState | undefined,
                  ticket.slaStatus?.paused,
                  ticket.slaRemainingMins,
                );
                return (
                  <tr
                    key={ticket.id}
                    onClick={() => navigate(`${SERVICE_DESK_BASE}/tickets/${ticket.id}`)}
                    className={`cursor-pointer border-t border-line hover:bg-subtle`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{ticket.reference}</td>
                    <td className={`px-4 py-2.5 font-medium ${text}`}>
                      {ticket.title}
                      {ticket.escalated && <span className="ml-2 text-[11px] font-semibold text-red-500">{t("ticketsList.table.escalated")}</span>}
                      {ticket.sandbox && <span className="ml-2 align-middle"><SampleChip ticket={ticket} /></span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {ticket.requester?.name || "—"}
                      {ticket.companyName && <span className="block text-[11px] text-gray-400">{ticket.companyName}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{ticket.brandLabel}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <StatusBadge label={ticket.statusLabel || statusFallback(ticket.status)?.label || ticket.status} color={statusColor(ticket.status)} />
                        <AiAgentBadge state={ticket.aiAgent?.state} />
                        <ReworkChip count={ticket.rework?.count} />
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><PriorityBadge priority={ticket.priority} /></td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <SlaBadge state={sla.state} />
                        {sla.text && (
                          <span className={`text-xs whitespace-nowrap ${sla.overdue ? "font-semibold text-red-500" : "text-gray-400"}`}>{sla.text}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatMins(ticket.ageMins)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatMins(ticket.resolutionMins)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{formatDate(ticket.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Controles de paginación fuera del scroll */}
        {!loadError && !loading && rows.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line">
            <div className="text-sm text-gray-500">
              {t("ticketsList.pagination.showing", { 
                start: (safePage - 1) * itemsPerPage + 1, 
                end: Math.min(safePage * itemsPerPage, rows.length), 
                total: rows.length 
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, Math.min(p, totalPages) - 1))}
                disabled={safePage === 1}
                className={`px-3 py-1 text-sm rounded ${safePage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-700"} text-content-secondary`}
              >
                ← {t("ticketsList.pagination.previous")}
              </button>
              <span className={`text-sm text-content-secondary`}>
                {t("ticketsList.pagination.page", { current: safePage, total: totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, Math.min(p, totalPages) + 1))}
                disabled={safePage === totalPages}
                className={`px-3 py-1 text-sm rounded ${safePage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-700"} text-content-secondary`}
              >
                {t("ticketsList.pagination.next")} →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Sortable header cell. Shows a ▲/▼ caret on the active column.
function Th({ label, col, sort, onSort, title }: {
  label: string; col: SortKey; sort: { key: SortKey; dir: 1 | -1 }; onSort: (k: SortKey) => void; title?: string;
}) {
  const active = sort.key === col;
  return (
    <th className="px-4 py-2" title={title}>
      <button onClick={() => onSort(col)} className={`inline-flex items-center gap-1 uppercase ${active ? "text-gray-600" : ""}`}>
        {label}
        <span className="text-[10px]">{active ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}
